import { useMemo, useState } from 'react';
import {
  MasterListFilter,
  MasterListFilterKeys,
  MasterListJournal,
} from '../../../types/MasterList.types';

const emptyFilters: MasterListFilter = {
  licenses: [],
  publishingModels: [],
  publishers: [],
  imprints: [],
  statuses: [],
};

interface UseMasterListFiltersParams {
  journals: MasterListJournal[];
  searchTerm: string;
}

export const useMasterListFilters = ({
  journals,
  searchTerm,
}: UseMasterListFiltersParams) => {
  const [selectedFilters, setSelectedFilters] =
    useState<MasterListFilter>(emptyFilters);

  const searchedJournals = useMemo(() => {
    if (searchTerm.trim() === '') return journals;

    const normalizedSearch = searchTerm.trim().toLowerCase();

    return journals.filter(
      (journal) =>
        journal?.name?.toLowerCase().includes(normalizedSearch) ||
        journal?.publisher?.toLowerCase().includes(normalizedSearch) ||
        journal?.issn_e?.toLowerCase().includes(normalizedSearch) ||
        journal?.issn_p?.toLowerCase().includes(normalizedSearch)
    );
  }, [journals, searchTerm]);

  const availableFilters = useMemo<MasterListFilter>(() => {
    const uniquePublishers = new Set<string>();
    const uniqueImprints = new Set<string>();
    const uniqueLicenses = new Set<string>();
    const uniquePublishingModels = new Set<string>();
    const uniqueStatuses = new Set<string>();

    journals.forEach((journal) => {
      if (journal.publisher) {
        uniquePublishers.add(journal.publisher);
      }
      if (journal.imprint) {
        uniqueImprints.add(journal.imprint);
      }
      if (journal.publishing_model) {
        uniquePublishingModels.add(journal.publishing_model);
      }
      if (journal.status) {
        uniqueStatuses.add(journal.status);
      }
      journal.licenses?.forEach((license) => {
        if (license) {
          uniqueLicenses.add(license);
        }
      });
    });

    return {
      publishers: Array.from(uniquePublishers).sort(),
      imprints: Array.from(uniqueImprints).sort(),
      licenses: Array.from(uniqueLicenses).sort(),
      publishingModels: Array.from(uniquePublishingModels).sort(),
      statuses: Array.from(uniqueStatuses).sort(),
    };
  }, [journals]);

  const hasFiltersApplied = useMemo(
    () =>
      Object.values(selectedFilters).some(
        (selectedValues) => selectedValues.length > 0
      ),
    [selectedFilters]
  );

  const filteredJournals = useMemo(() => {
    if (!hasFiltersApplied) return searchedJournals;

    return searchedJournals.filter((journal) => {
      const matchesPublisher =
        selectedFilters.publishers.length === 0 ||
        (!!journal.publisher &&
          selectedFilters.publishers.includes(journal.publisher));
      const matchesImprint =
        selectedFilters.imprints.length === 0 ||
        (!!journal.imprint &&
          selectedFilters.imprints.includes(journal.imprint));
      const matchesPublishingModel =
        selectedFilters.publishingModels.length === 0 ||
        (!!journal.publishing_model &&
          selectedFilters.publishingModels.includes(journal.publishing_model));
      const matchesStatus =
        selectedFilters.statuses.length === 0 ||
        (!!journal.status && selectedFilters.statuses.includes(journal.status));
      const matchesLicenses =
        selectedFilters.licenses.length === 0 ||
        (journal.licenses?.some((license) =>
          selectedFilters.licenses.includes(license)
        ) ??
          false);

      return (
        matchesPublisher &&
        matchesImprint &&
        matchesPublishingModel &&
        matchesStatus &&
        matchesLicenses
      );
    });
  }, [hasFiltersApplied, searchedJournals, selectedFilters]);

  const clearAllFilters = () => {
    setSelectedFilters(emptyFilters);
  };

  const handleToggleFilter = (type: MasterListFilterKeys, value: string) => {
    setSelectedFilters((prevFilters) => {
      const valueIsSelected = prevFilters[type].includes(value);

      return {
        ...prevFilters,
        [type]: valueIsSelected
          ? prevFilters[type].filter((selectedValue) => selectedValue !== value)
          : [...prevFilters[type], value],
      };
    });
  };

  return {
    availableFilters,
    selectedFilters,
    hasFiltersApplied,
    filteredJournals,
    clearAllFilters,
    handleToggleFilter,
  };
};
